/**
 * Image Lightbox with Auto-Scroll Pause
 * Opens product images in full-screen modal
 * Pauses carousel auto-scroll when lightbox is open
 */

(function() {
  'use strict';

  class ImageLightbox {
    constructor() {
      this.lightbox = null;
      this.currentIndex = 0;
      this.images = [];
      this.carousel = null;
      this.init();
    }

    init() {
      // Create lightbox HTML
      this.createLightbox();
      
      // Attach click handlers to all product images
      this.attachImageHandlers();
      
      // Keyboard navigation
      document.addEventListener('keydown', (e) => this.handleKeyboard(e));
    }

    createLightbox() {
      const lightboxHTML = `
        <div id="imageLightbox" class="image-lightbox">
          <div class="lightbox-overlay"></div>
          <div class="lightbox-content">
            <button class="lightbox-close" aria-label="Close lightbox">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
            
            <button class="lightbox-nav lightbox-prev" aria-label="Previous image">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="15 18 9 12 15 6"></polyline>
              </svg>
            </button>
            
            <button class="lightbox-nav lightbox-next" aria-label="Next image">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
            
            <div class="lightbox-image-container">
              <img src="" alt="" class="lightbox-image" id="lightboxImage">
            </div>
            
            <div class="lightbox-counter" id="lightboxCounter"></div>
            
            <div class="lightbox-thumbnails" id="lightboxThumbnails"></div>
          </div>
        </div>
      `;
      
      document.body.insertAdjacentHTML('beforeend', lightboxHTML);
      this.lightbox = document.getElementById('imageLightbox');
      
      // Attach lightbox event listeners
      this.lightbox.querySelector('.lightbox-close').addEventListener('click', () => this.close());
      this.lightbox.querySelector('.lightbox-overlay').addEventListener('click', () => this.close());
      this.lightbox.querySelector('.lightbox-prev').addEventListener('click', () => this.showPrev());
      this.lightbox.querySelector('.lightbox-next').addEventListener('click', () => this.showNext());
    }

    attachImageHandlers() {
      // Find all product carousels and attach click handlers
      const productCarousels = document.querySelectorAll('[data-product-carousel]');
      
      productCarousels.forEach(carousel => {
        const images = carousel.querySelectorAll('.carousel-slide img, .product-featured-image');
        
        images.forEach((img, index) => {
          img.style.cursor = 'zoom-in';
          
          img.addEventListener('click', (e) => {
            // Don't open lightbox if clicking carousel nav buttons
            if (e.target.closest('.carousel-nav, .carousel-indicators')) {
              return;
            }
            
            e.preventDefault();
            e.stopPropagation();
            
            this.carousel = carousel;
            this.open(carousel, index);
          });
        });
      });
      
      console.log('✅ Lightbox attached to', productCarousels.length, 'carousels');
    }

    open(carousel, startIndex = 0) {
      // Collect all images from the carousel
      const imageElements = carousel.querySelectorAll('.carousel-slide img, .product-featured-image');
      this.images = Array.from(imageElements).map(img => ({
        src: img.src.replace(/_(\d+x\d+|pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048)\./g, '_2048x.'),
        alt: img.alt
      }));
      
      if (this.images.length === 0) return;
      
      this.currentIndex = startIndex;
      
      // Pause carousel auto-scroll
      this.pauseCarousel();
      
      // Show lightbox
      this.lightbox.classList.add('active');
      document.body.style.overflow = 'hidden';
      
      // Display current image
      this.updateImage();
      
      console.log('🖼️ Lightbox opened at image', startIndex + 1, 'of', this.images.length);
    }

    close() {
      this.lightbox.classList.remove('active');
      document.body.style.overflow = '';
      
      // Resume carousel auto-scroll after a delay
      setTimeout(() => {
        this.resumeCarousel();
      }, 300);
      
      console.log('❌ Lightbox closed');
    }

    showPrev() {
      this.currentIndex = (this.currentIndex - 1 + this.images.length) % this.images.length;
      this.updateImage();
    }

    showNext() {
      this.currentIndex = (this.currentIndex + 1) % this.images.length;
      this.updateImage();
    }

    updateImage() {
      const img = document.getElementById('lightboxImage');
      const counter = document.getElementById('lightboxCounter');
      const thumbnails = document.getElementById('lightboxThumbnails');
      
      // Update main image
      img.src = this.images[this.currentIndex].src;
      img.alt = this.images[this.currentIndex].alt;
      
      // Update counter
      counter.textContent = `${this.currentIndex + 1} / ${this.images.length}`;
      
      // Update thumbnails
      if (this.images.length > 1) {
        thumbnails.innerHTML = this.images.map((image, index) => `
          <div class="lightbox-thumbnail ${index === this.currentIndex ? 'active' : ''}" data-index="${index}">
            <img src="${image.src.replace(/_2048x\./g, '_small.')}" alt="${image.alt}">
          </div>
        `).join('');
        
        // Attach thumbnail click handlers
        thumbnails.querySelectorAll('.lightbox-thumbnail').forEach(thumb => {
          thumb.addEventListener('click', () => {
            this.currentIndex = parseInt(thumb.dataset.index);
            this.updateImage();
          });
        });
      } else {
        thumbnails.innerHTML = '';
      }
      
      // Hide/show nav buttons
      const prevBtn = this.lightbox.querySelector('.lightbox-prev');
      const nextBtn = this.lightbox.querySelector('.lightbox-next');
      
      if (this.images.length <= 1) {
        prevBtn.style.display = 'none';
        nextBtn.style.display = 'none';
      } else {
        prevBtn.style.display = 'flex';
        nextBtn.style.display = 'flex';
      }
    }

    handleKeyboard(e) {
      if (!this.lightbox.classList.contains('active')) return;
      
      switch(e.key) {
        case 'Escape':
          this.close();
          break;
        case 'ArrowLeft':
          this.showPrev();
          break;
        case 'ArrowRight':
          this.showNext();
          break;
      }
    }

    pauseCarousel() {
      if (!this.carousel) return;
      
      // Find carousel instance and pause it
      const carouselElement = this.carousel.querySelector('.product-carousel');
      if (carouselElement && carouselElement._carouselInstance) {
        carouselElement._carouselInstance.stopAutoScroll();
        console.log('⏸️ Carousel auto-scroll paused for lightbox');
      }
    }

    resumeCarousel() {
      if (!this.carousel) return;
      
      // Find carousel instance and resume it
      const carouselElement = this.carousel.querySelector('.product-carousel');
      if (carouselElement && carouselElement._carouselInstance) {
        carouselElement._carouselInstance.startAutoScroll();
        console.log('▶️ Carousel auto-scroll resumed after lightbox');
      }
    }
  }

  // Initialize lightbox when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.imageLightbox = new ImageLightbox();
    });
  } else {
    window.imageLightbox = new ImageLightbox();
  }

  // Re-initialize on dynamic content load
  document.addEventListener('shopify:section:load', () => {
    if (window.imageLightbox) {
      window.imageLightbox.attachImageHandlers();
    }
  });

})();